import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/api';
import { LoadingSpinner } from '../ui/Icons';

const PartyPlayer = ({ playerId, pin, nickname, onExit }) => {
    const [gameState, setGameState] = useState(null);
    const [submittedAnswer, setSubmittedAnswer] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const interval = setInterval(loadStatus, 1500); // 1.5s polling (slightly faster for responsiveness)
        loadStatus();
        return () => clearInterval(interval);
    }, []);

    const loadStatus = async () => {
        try {
            const res = await fetch(`${API_URL}/api/party/status/?pin=${pin}&player_id=${playerId}`);
            if (res.ok) {
                const data = await res.json();
                setGameState(data);

                // Sync local state if server says answered
                if (data.has_answered && !submittedAnswer) {
                    // We don't know WHAT we answered if we reloaded, but we know we did.
                    setSubmittedAnswer('?');
                }
                // Reset on new round (if server moved to question but we have an answer stored from prev round)
                if (data.status === 'QUESTION' && !data.has_answered && submittedAnswer === '?') {
                    setSubmittedAnswer(null);
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const submitAnswer = async (answer) => {
        if (submittedAnswer) return;
        setSubmittedAnswer(answer); // Optimistic UI

        try {
            await fetch(`${API_URL}/api/party/submit/`, {
                method: 'POST',
                body: JSON.stringify({ player_id: playerId, answer })
            });
        } catch (e) {
            console.error(e);
            setSubmittedAnswer(null); // Revert on error
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center"><LoadingSpinner /></div>;

    // WAITING / FINISHED
    if (gameState?.status === 'WAITING' || gameState?.status === 'COUNTDOWN') {
        return (
            <div className="h-screen bg-indigo-900 text-white flex flex-col items-center justify-center p-6 text-center">
                <div className="text-4xl mb-4">You're in!</div>
                <div className="text-xl font-bold mb-8">{nickname}</div>
                <div className="text-indigo-300 animate-pulse">See your name on screen?</div>
            </div>
        );
    }

    if (gameState?.status === 'FINISHED') {
        // Calculate player's rank
        const sortedPlayers = gameState.players_list?.sort((a, b) => b.score - a.score) || [];
        const playerRank = sortedPlayers.findIndex(p => p.nickname === nickname) + 1;

        return (
            <div className="h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6 text-center">
                <div className="text-6xl mb-6">
                    {playerRank === 1 ? '👑' : playerRank === 2 ? '🥈' : playerRank === 3 ? '🥉' : '🎉'}
                </div>
                <div className="text-3xl font-bold mb-2">
                    {playerRank === 1 ? 'Du hast gewonnen!' : `#${playerRank} Platz`}
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4 my-8 w-full max-w-md">
                    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                        <div className="text-slate-400 text-sm mb-1">Punkte</div>
                        <div className="text-4xl font-black text-plantlix-400">{gameState.my_score}</div>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                        <div className="text-slate-400 text-sm mb-1">Max Streak</div>
                        <div className="text-4xl font-black text-orange-400">🔥 {gameState.my_streak}</div>
                    </div>
                </div>

                <div className="text-slate-400 text-sm mb-2">von {gameState.players_count} Spielern</div>
                <button onClick={onExit} className="mt-4 px-8 py-3 bg-white text-slate-900 rounded-full font-bold hover:scale-105 transition-transform">Verlassen</button>
            </div>
        );
    }

    // GAMEPLAY
    const isQuestion = gameState?.status === 'QUESTION';
    const isReveal = gameState?.status === 'REVEAL';

    if (isReveal) {
        return (
            <div className={`h-screen flex flex-col items-center justify-center p-6 text-center text-white transition-colors duration-500 ${gameState.was_correct ? 'bg-plantlix-600' : 'bg-red-600'}`}>
                <div className="text-6xl mb-4">{gameState.was_correct ? 'Correct!' : 'Wrong!'}</div>
                <div className="text-2xl opacity-80">Streak: {gameState.my_streak} 🔥</div>
                <div className="mt-8 text-xl font-bold">Current Score: {gameState.my_score}</div>
            </div>
        );
    }

    // QUESTION PHASE
    // Calculate live ranking
    const sortedPlayers = gameState?.players_list?.sort((a, b) => b.score - a.score) || [];
    const playerRank = sortedPlayers.findIndex(p => p.nickname === nickname) + 1;

    return (
        <div className="h-screen bg-slate-100 flex flex-col p-2">
            {/* Enhanced Header with Ranking */}
            <div className="bg-white p-4 rounded-xl shadow-sm mb-2">
                <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-slate-700">{nickname}</span>
                    <span className="bg-slate-100 px-3 py-1 rounded-full text-slate-600 text-sm font-mono">{gameState?.my_score} pts</span>
                </div>
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-slate-500 text-sm">Platzierung:</span>
                        <span className={`font-black text-lg ${playerRank <= 3 ? 'text-yellow-500' : 'text-slate-700'}`}>
                            #{playerRank}
                            {playerRank === 1 && ' 👑'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-slate-500 text-sm">Streak:</span>
                        <span className="font-bold text-orange-500">🔥 {gameState?.my_streak}</span>
                    </div>
                </div>
            </div>

            {submittedAnswer ? (
                <div className="flex-1 flex items-center justify-center flex-col">
                    <div className="text-2xl font-bold text-slate-400 animate-bounce mb-4">Antwort gesendet!</div>
                    {/* Show current stats while waiting */}
                    <div className="bg-white rounded-xl p-6 shadow-lg">
                        <div className="text-center">
                            <div className="text-slate-500 text-sm mb-2">Aktuelle Position</div>
                            <div className={`text-5xl font-black mb-4 ${playerRank <= 3 ? 'text-yellow-500' : 'text-slate-700'}`}>
                                #{playerRank}
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div className="text-slate-400">Punkte</div>
                                    <div className="font-bold text-lg text-plantlix-600">{gameState?.my_score}</div>
                                </div>
                                <div>
                                    <div className="text-slate-400">Streak</div>
                                    <div className="font-bold text-lg text-orange-500">🔥 {gameState?.my_streak}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 grid grid-cols-2 gap-2">
                    <button onClick={() => submitAnswer('A')} className="bg-red-500 rounded-xl shadow-md active:scale-95 transition-transform flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">A</span>
                    </button>
                    <button onClick={() => submitAnswer('B')} className="bg-blue-500 rounded-xl shadow-md active:scale-95 transition-transform flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">B</span>
                    </button>
                    <button onClick={() => submitAnswer('C')} className="bg-yellow-500 rounded-xl shadow-md active:scale-95 transition-transform flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">C</span>
                    </button>
                    <button onClick={() => submitAnswer('D')} className="bg-plantlix-500 rounded-xl shadow-md active:scale-95 transition-transform flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">D</span>
                    </button>
                </div>
            )}

            <div className="mt-2 text-center text-xs text-slate-400">
                Schau auf den Host-Bildschirm für die Frage!
            </div>
        </div>
    );
};

export default PartyPlayer;
